/**
 * Route permission metadata — the ONE place a route says who may call it.
 *
 * Every ts-rest route in this package carries `metadata: routeMeta(...)`. The API adapter
 * (`apps/api/src/lib/ts-rest.ts`) reads it and gates entry with `canAnywhere` before the handler
 * runs; the handler's own scoped `can()` against the loaded record remains the record-level check.
 * The AI copilot derives its tool list from the same field, and the web/mobile apps may gate
 * navigation from it. One declaration, enforced at runtime, read by everything else — so it cannot
 * quietly drift the way a hand-kept sidecar map would.
 *
 * Values:
 *   - a `Permission`            — the caller must hold it at SOME scope (any-of when an array)
 *   - `null`                    — signed-in only; every authenticated user may call it
 *   - `'public'`                — no session at all (login, careers pages)
 *
 * Beyond who may call it, a route may also say what it returns:
 *   - `salaryPin`               — the response carries pay behind the salary PIN; see ADR 0041
 *
 * Adding a route without metadata fails `contracts/meta.test.ts` and throws when the API mounts
 * the router. That is deliberate: a missing declaration is a review question, not a default.
 */

import type { Permission } from '../permissions/permissions.js';

export type RoutePermission = Permission | readonly Permission[] | null | 'public';

export interface RouteMeta {
  readonly permission: RoutePermission;
  /**
   * Keep this route out of the AI copilot's tool list even though the caller may hold the
   * permission. Two reasons qualify:
   *
   *   1. The RESPONSE is unsafe to put in a prompt — a read returning decrypted PII (PAN,
   *      Aadhaar, bank details). The copilot can still navigate a user to the screen; it just
   *      never pulls the values into a prompt.
   *   2. The REQUEST is unsatisfiable by a model — a route whose body carries file bytes
   *      (`/documents/parse/*`). A derived tool would ask the model for a base64 file and get
   *      hallucinated bytes or an exhausted output budget. Such routes reach the copilot through
   *      a hand-written tool that takes an attachment id instead. The same applies to a body
   *      that is a large parsed table rather than a chat action (`POST /projects/tasks/bulk-import`,
   *      up to 500 CSV rows).
   *   3. A HAND-WRITTEN tool does the job correctly and the derived one cannot — the payroll run
   *      steps carry `expectedVersion`, which a derived proposal freezes at proposal time;
   *      `payroll_run_step` binds it at approval instead.
   *
   * `'read'` is the opposite case: a POST that CHANGES NOTHING — a preview, a validation, an export
   * computed on the fly. It is POST only because its input is a body. The copilot runs it straight
   * away like a GET and reads the answer, instead of turning it into a proposal whose result the
   * model never sees. Only for routes with no side effect beyond an audit line.
   */
  readonly ai?: 'hidden' | 'read';
  /**
   * The response (or the file it links to) carries PAY — a salary, a payslip, a run total, an offer CTC — and so
   * may only be served to a caller who has entered their salary PIN in this login session in the last five
   * minutes (ADR 0041). Absent means the route carries no one's pay.
   *
   *   - `'others'`  — always someone else's pay (a register, a list, a run, an export). The API adapter checks
   *                   BEFORE the handler runs, so a tagged write changes nothing while salary is locked.
   *   - `'subject'` — one person's pay, and that person may be the caller (a compensation history, a payslip
   *                   preview). The handler calls `requireSalaryPinFor(req, subjectPersonId)` once it knows
   *                   whose record it loaded; the caller's own record is exempt. The adapter refuses to send the
   *                   response of a `'subject'` route whose handler never made that call.
   *
   * Only callers holding one of `SALARY_PIN_PERMISSIONS` are ever asked. `contracts/salary-pin-meta.test.ts`
   * makes every route behind those permissions either carry this tag or sit on `salary-pin.reviewed.ts`.
   */
  readonly salaryPin?: SalaryPinGate;
}

/** Which salary-PIN gate a pay-bearing route sits behind — see `RouteMeta.salaryPin` and ADR 0041. */
export type SalaryPinGate = 'others' | 'subject';

export function routeMeta(
  permission: RoutePermission,
  extra: Omit<RouteMeta, 'permission'> = {},
): RouteMeta {
  return { permission, ...extra };
}

/** Structural check used by the adapter and the copilot; `undefined` metadata is NOT a RouteMeta. */
export function isRouteMeta(value: unknown): value is RouteMeta {
  if (typeof value !== 'object' || value === null || !('permission' in value)) return false;
  const p = (value as { permission: unknown }).permission;
  return p === null || p === 'public' || typeof p === 'string' || Array.isArray(p);
}

/** The permissions a caller needs (any-of), or `[]` for `null`/`'public'`. */
export function metaPermissions(meta: RouteMeta): readonly Permission[] {
  const p = meta.permission;
  if (p === null || p === 'public') return [];
  return typeof p === 'string' ? [p] : p;
}

/** The route's salary-PIN gate, or null when it carries no pay. */
export function metaSalaryPin(meta: RouteMeta): SalaryPinGate | null {
  return meta.salaryPin ?? null;
}
